import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'trip-explorer:custom-exclusions';

/** A single user-defined exclusion rule. For v1 the only matcher is a
 *  case-insensitive substring match on the place name; the discriminated
 *  shape leaves room to add `addressContains`, `categoryEquals`, etc.
 *  later without breaking the persisted format. */
export interface CustomExclusion {
  /** Stable id for list-keying + delete. */
  id: string;
  /** Match strategy. Today only `name-contains`. */
  kind: 'name-contains';
  /** The substring or pattern to match against. */
  value: string;
}

const readStored = (): Array<CustomExclusion> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is CustomExclusion =>
        x != null &&
        typeof x === 'object' &&
        typeof x.id === 'string' &&
        typeof x.value === 'string' &&
        x.kind === 'name-contains',
    );
  } catch {
    return [];
  }
};

const writeStored = (list: Array<CustomExclusion>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Quota or disabled storage — silently ignore. Selection will reset on
    // next load, which is acceptable.
  }
};

// ─── Module-level store ──────────────────────────────────────────────
//
// The hook needs to share state across every consumer in the tree —
// otherwise the FiltersPopover instance and the App instance hold
// independent copies of the list and changes in one don't update the
// other. We use a tiny pub/sub backed by `useSyncExternalStore` to keep
// all subscribers reading the same array reference.
//
// Snapshot identity must be stable between unchanged calls (React calls
// `getSnapshot` during render and bails out if the result === previous),
// so we cache the array and only swap the reference on actual change.
let snapshot: Array<CustomExclusion> = readStored();
const subscribers = new Set<() => void>();

const notify = (): void => {
  for (const sub of subscribers) sub();
};

const setSnapshot = (next: Array<CustomExclusion>): void => {
  snapshot = next;
  writeStored(next);
  notify();
};

const subscribe = (cb: () => void): (() => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

// Cross-tab sync — a `storage` event fires in OTHER tabs (not the one
// that called `setItem`), so this only handles multi-tab edits. Same-tab
// updates go through `setSnapshot` directly. Listener attaches once at
// module load.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    snapshot = readStored();
    notify();
  });
}

const getSnapshot = (): Array<CustomExclusion> => snapshot;

export interface CustomExclusionsApi {
  exclusions: Array<CustomExclusion>;
  /** Add a name-substring exclusion. Trims + dedups (case-insensitive)
   *  so the same value can't be added twice. No-op on empty input. */
  addNameContains: (value: string) => void;
  /** Remove by id. No-op if id isn't in the list. */
  remove: (id: string) => void;
}

export const useCustomExclusions = (): CustomExclusionsApi => {
  const exclusions = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const addNameContains = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    if (snapshot.some((e) => e.kind === 'name-contains' && e.value.toLowerCase() === lower)) {
      return;
    }
    setSnapshot([...snapshot, { id: crypto.randomUUID(), kind: 'name-contains', value: trimmed }]);
  }, []);

  const remove = useCallback((id: string) => {
    const next = snapshot.filter((e) => e.id !== id);
    if (next.length === snapshot.length) return;
    setSnapshot(next);
  }, []);

  return { exclusions, addNameContains, remove };
};

/** Pure predicate: does `name` match any of the user's exclusions? */
export const matchesExclusion = (
  name: string,
  exclusions: ReadonlyArray<CustomExclusion>,
): boolean => {
  if (exclusions.length === 0) return false;
  const lower = name.toLowerCase();
  for (const ex of exclusions) {
    if (ex.kind === 'name-contains' && lower.includes(ex.value.toLowerCase())) {
      return true;
    }
  }
  return false;
};
